import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Lock, ArrowRight, BarChart3, RefreshCw, LogOut, Users, FileText, TrendingUp, Globe, ExternalLink, DollarSign, Save } from 'lucide-react';
import { Logo } from '../components/Logo';
import { invalidateJourneys } from '../lib/useJourneys';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'tdt_admin_token';
const tokenStore = window.sessionStorage;

const SITE_NAV = [
  { to: '/', label: 'Home' },
  { to: '/private-experiences', label: 'Private Experiences' },
  { to: '/signature-journeys', label: 'Signature Journeys' },
  { to: '/corporate', label: 'Corporate' },
  { to: '/gift', label: 'Gift' },
  { to: '/about', label: 'About' },
  { to: '/enquire', label: 'Enquire' },
];

const TABS = [
  { id: 'overview',  label: 'Overview',  icon: BarChart3 },
  { id: 'visits',    label: 'Visits',    icon: Users },
  { id: 'trends',    label: 'Trends',    icon: TrendingUp },
  { id: 'sources',   label: 'Sources',   icon: Globe },
  { id: 'enquiries', label: 'Enquiries', icon: FileText },
  { id: 'pricing',   label: 'Pricing',   icon: DollarSign },
];

export default function Admin() {
  const [status, setStatus] = useState('checking');
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    const token = tokenStore.getItem(TOKEN_KEY);
    if (!token) { setStatus('locked'); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/check`, { headers: { Authorization: `Bearer ${token}` } });
        setStatus(data?.unlocked ? 'unlocked' : 'locked');
        if (!data?.unlocked) tokenStore.removeItem(TOKEN_KEY);
      } catch { tokenStore.removeItem(TOKEN_KEY); setStatus('locked'); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) { setError('Please enter the admin passcode.'); return; }
    setSubmitting(true); setError('');
    try {
      const { data } = await axios.post(`${API}/admin/access`, { passcode: passcode.trim() });
      if (data?.token) {
        tokenStore.setItem(TOKEN_KEY, data.token);
        setStatus('unlocked');
      }
    } catch (err) {
      setError(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Incorrect passcode.');
    } finally { setSubmitting(false); }
  };

  const signOut = () => { tokenStore.removeItem(TOKEN_KEY); setStatus('locked'); };

  if (status === 'checking') {
    return <div className="fixed inset-0 flex items-center justify-center bg-[color:var(--tdt-ivory)]"><div className="w-2 h-2 rounded-full bg-[color:var(--tdt-gold)] animate-pulse" /></div>;
  }

  if (status === 'locked') {
    return (
      <div data-testid="admin-gate" className="min-h-screen bg-[color:var(--tdt-ink)] text-[color:var(--tdt-ivory)] flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md text-center">
          <div className="mb-10 inline-flex"><Logo size="md" tone="light" /></div>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-[color:var(--tdt-gold)]/40 text-[color:var(--tdt-gold)] mb-6"><Lock size={18} /></div>
          <div className="tdt-eyebrow tdt-eyebrow-light mb-4">Admin Access</div>
          <h1 className="font-serif font-light text-3xl lg:text-4xl leading-snug mb-4">Visit insights.</h1>
          <p className="text-sm text-[color:var(--tdt-cream)]/85 mb-10">Enter your admin passcode to view analytics.</p>
          <form onSubmit={submit} noValidate className="text-left">
            <label className="block">
              <span className="tdt-label !text-[color:var(--tdt-cream)]/70">Admin passcode</span>
              <input
                data-testid="admin-passcode-input"
                type="password"
                autoFocus
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Enter passcode"
                className="w-full bg-transparent border-b border-[color:var(--tdt-border-light)] focus:border-[color:var(--tdt-gold)] outline-none py-3 px-1 text-[color:var(--tdt-ivory)] placeholder:text-[color:var(--tdt-cream)]/35 tracking-[0.18em] uppercase text-center"
                style={{ letterSpacing: '0.2em' }}
              />
            </label>
            {error && <p data-testid="admin-error" className="mt-4 text-xs uppercase tracking-[0.22em] text-[#e8a09a] text-center">{error}</p>}
            <button data-testid="admin-submit" type="submit" disabled={submitting} className="btn-pill btn-gold mt-10 w-full disabled:opacity-60">
              {submitting ? 'Verifying\u2026' : <>Sign In <ArrowRight size={14} /></>}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[color:var(--tdt-ivory)]">
      <header className="bg-[color:var(--tdt-ink)] text-[color:var(--tdt-ivory)] sticky top-0 z-20 border-b border-[color:var(--tdt-border-light)]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <Link to="/" className="flex items-center gap-4 hover:opacity-90 transition-opacity">
            <Logo size="sm" tone="light" />
            <span className="tdt-eyebrow tdt-eyebrow-light hidden sm:inline">Admin</span>
          </Link>
          <div className="flex items-center gap-3 self-start sm:self-auto">
            <Link to="/" data-testid="admin-view-site" className="btn-pill btn-outline-ivory text-xs">
              <ExternalLink size={14} /> View site
            </Link>
            <button data-testid="admin-signout" onClick={signOut} className="btn-pill btn-outline-ivory text-xs">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        {/* Site nav (so admin can jump straight to a public page) */}
        <nav className="border-t border-[color:var(--tdt-border-light)]/50 overflow-x-auto">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12 flex gap-5 sm:gap-7 py-3">
            {SITE_NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`admin-site-nav-${n.to.replace('/', '') || 'home'}`}
                className="text-[11px] uppercase tracking-[0.2em] whitespace-nowrap text-[color:var(--tdt-cream)]/65 hover:text-[color:var(--tdt-gold)] transition-colors"
              >
                {n.label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Admin tabs */}
        <nav className="border-t border-[color:var(--tdt-border-light)]/50 overflow-x-auto bg-[color:var(--tdt-ink-soft)]">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12 flex gap-6 sm:gap-8">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`py-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] whitespace-nowrap transition-colors ${active ? 'text-[color:var(--tdt-gold)] border-b border-[color:var(--tdt-gold)]' : 'text-[color:var(--tdt-cream)]/70 hover:text-[color:var(--tdt-cream)]'}`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-14">
        {tab === 'overview'  && <Overview />}
        {tab === 'visits'    && <Visits />}
        {tab === 'trends'    && <Trends />}
        {tab === 'sources'   && <Sources />}
        {tab === 'enquiries' && <Enquiries />}
        {tab === 'pricing'   && <Pricing />}
      </main>
    </div>
  );
}

// ============ shared helpers ============
const useFetch = (url, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = async () => {
    setLoading(true); setError(null);
    try {
      const token = tokenStore.getItem(TOKEN_KEY);
      const { data } = await axios.get(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, deps);
  return { data, loading, error, reload };
};

function SectionHeader({ eyebrow, title, onRefresh, loading }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        <div className="tdt-eyebrow mb-3">{eyebrow}</div>
        <h2 className="font-serif font-light text-3xl sm:text-4xl text-[color:var(--tdt-ink)]">{title}</h2>
      </div>
      {onRefresh && (
        <button data-testid="admin-refresh" onClick={onRefresh} disabled={loading} className="btn-pill btn-outline-ink text-xs self-start sm:self-auto disabled:opacity-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[color:var(--tdt-cream)] rounded-md p-6 sm:p-8">
      <div className="tdt-eyebrow mb-3">{label}</div>
      <div className="font-serif text-4xl sm:text-5xl text-[color:var(--tdt-ink)]">{value}</div>
    </div>
  );
}

function BarRow({ label, value, max, testid }) {
  const pct = Math.max(2, Math.round((value / Math.max(max, 1)) * 100));
  return (
    <div data-testid={testid} className="py-4 grid grid-cols-12 items-center gap-4">
      <div className="col-span-7 sm:col-span-5 font-serif text-base sm:text-lg text-[color:var(--tdt-ink)] truncate">{label}</div>
      <div className="col-span-3 sm:col-span-5 hidden sm:block">
        <div className="h-1.5 rounded-full bg-[color:var(--tdt-border)] overflow-hidden">
          <div className="h-full bg-[color:var(--tdt-gold)]" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="col-span-5 sm:col-span-2 text-right font-serif text-lg sm:text-xl text-[color:var(--tdt-ink)]">{value.toLocaleString()}</div>
    </div>
  );
}

// ============ Overview ============
function Overview() {
  const { data, loading, reload } = useFetch('/admin/visits/summary');
  return (
    <section data-testid="admin-overview">
      <SectionHeader eyebrow="Visit Insights" title="Overview" onRefresh={reload} loading={loading} />
      <div className="grid sm:grid-cols-2 gap-5 sm:gap-6 mb-10">
        <Stat label="Total visits" value={data?.total ?? '\u2014'} />
        <Stat label="Unique sessions" value={data?.unique_sessions ?? '\u2014'} />
      </div>
      <div className="bg-[color:var(--tdt-cream)] rounded-md p-6 sm:p-10">
        <div className="tdt-eyebrow mb-5">Top pages</div>
        {data?.per_page?.length ? (
          <div className="divide-y divide-[color:var(--tdt-border)]">
            {data.per_page.slice(0, 8).map((row) => (
              <BarRow key={row.path} label={row.path} value={row.count} max={data.per_page[0].count} testid={`overview-row-${row.path}`} />
            ))}
          </div>
        ) : <p className="text-sm text-[color:var(--tdt-muted)]">{loading ? 'Loading\u2026' : 'No visits yet.'}</p>}
      </div>
    </section>
  );
}

// ============ Visits (per-page) ============
function Visits() {
  const { data, loading, reload } = useFetch('/admin/visits/summary');
  const max = data?.per_page?.[0]?.count || 1;
  return (
    <section data-testid="admin-visits">
      <SectionHeader eyebrow="Visits" title="Per-page counts" onRefresh={reload} loading={loading} />
      <div className="bg-[color:var(--tdt-cream)] rounded-md p-6 sm:p-10">
        {data?.per_page?.length ? (
          <div className="divide-y divide-[color:var(--tdt-border)]">
            {data.per_page.map((row) => (
              <BarRow key={row.path} label={row.path} value={row.count} max={max} testid={`visits-row-${row.path}`} />
            ))}
          </div>
        ) : <p className="text-sm text-[color:var(--tdt-muted)]">{loading ? 'Loading\u2026' : 'No visits yet.'}</p>}
      </div>
    </section>
  );
}

// ============ Trends ============
function Trends() {
  const { data, loading, reload } = useFetch('/admin/visits/daily?days=60');
  const series = data?.series || [];
  const maxVisits = Math.max(1, ...series.map(s => s.visits));
  // Build a 60-day timeline filling missing dates with 0
  const timeline = useMemo(() => {
    const days = 60;
    const today = new Date();
    today.setUTCHours(0,0,0,0);
    const map = new Map(series.map(r => [r.day, r]));
    const out = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today); d.setUTCDate(today.getUTCDate() - i);
      const key = d.toISOString().slice(0, 10);
      const r = map.get(key);
      out.push({ day: key, visits: r?.visits || 0, unique: r?.unique || 0 });
    }
    return out;
  }, [series]);

  const totalVisits = timeline.reduce((s, r) => s + r.visits, 0);
  const totalUnique = timeline.reduce((s, r) => s + r.unique, 0);

  return (
    <section data-testid="admin-trends">
      <SectionHeader eyebrow="Trends" title="Last 60 days" onRefresh={reload} loading={loading} />
      <div className="grid sm:grid-cols-2 gap-5 sm:gap-6 mb-10">
        <Stat label="Visits (60d)" value={totalVisits} />
        <Stat label="Unique sessions (60d)" value={totalUnique} />
      </div>
      <div className="bg-[color:var(--tdt-cream)] rounded-md p-6 sm:p-10">
        <div className="tdt-eyebrow mb-5">Daily visits</div>
        <Sparkbars data={timeline} maxVisits={maxVisits} />
      </div>
    </section>
  );
}

function Sparkbars({ data, maxVisits }) {
  const width = 1100;
  const height = 180;
  const padX = 24, padY = 12;
  const innerW = width - padX * 2;
  const barW = innerW / data.length;
  return (
    <div className="overflow-x-auto -mx-2">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[640px] h-44">
        {data.map((d, i) => {
          const h = Math.round(((d.visits || 0) / maxVisits) * (height - padY * 2));
          const x = padX + i * barW;
          const y = height - padY - h;
          return (
            <g key={d.day}>
              <rect x={x + 1} y={y} width={Math.max(2, barW - 2)} height={h || 1} fill="#b9924b" fillOpacity={d.visits ? 0.92 : 0.18} rx="1.5">
                <title>{d.day}: {d.visits} visits, {d.unique} unique</title>
              </rect>
              {/* date label every 10th day */}
              {i % 10 === 0 && (
                <text x={x + barW / 2} y={height - 1} fontSize="9" textAnchor="middle" fill="#6d6259">
                  {d.day.slice(5)}
                </text>
              )}
            </g>
          );
        })}
        <line x1={padX} y1={height - padY} x2={width - padX} y2={height - padY} stroke="#6d6259" strokeOpacity="0.2" strokeWidth="0.5" />
      </svg>
    </div>
  );
}

// ============ Sources ============
function Sources() {
  const { data, loading, reload } = useFetch('/admin/visits/sources');
  const sources = data?.sources || [];
  const max = sources[0]?.count || 1;
  return (
    <section data-testid="admin-sources">
      <SectionHeader eyebrow="Sources" title="Top referrers" onRefresh={reload} loading={loading} />
      <div className="bg-[color:var(--tdt-cream)] rounded-md p-6 sm:p-10">
        {sources.length ? (
          <div className="divide-y divide-[color:var(--tdt-border)]">
            {sources.map((row) => (
              <BarRow key={row.source} label={row.source} value={row.count} max={max} testid={`source-row-${row.source}`} />
            ))}
          </div>
        ) : <p className="text-sm text-[color:var(--tdt-muted)]">{loading ? 'Loading\u2026' : 'No source data yet. New visits will be tracked with their referring URL.'}</p>}
      </div>
    </section>
  );
}

// ============ Enquiries ============
const STATUS_OPTIONS = [
  { value: 'new',         label: 'New' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'complete',    label: 'Complete' },
];

const STATUS_STYLE = {
  new:         { dot: '#b9924b', text: 'text-[color:var(--tdt-gold)]',  ring: 'ring-[color:var(--tdt-gold)]/30' },
  in_progress: { dot: '#5a1e2e', text: 'text-[color:var(--tdt-burgundy)]', ring: 'ring-[color:var(--tdt-burgundy)]/30' },
  complete:    { dot: '#6b6845', text: 'text-[#6b6845]', ring: 'ring-[#6b6845]/30' },
};

function Enquiries() {
  const { data, loading, reload } = useFetch('/admin/enquiries');
  const rows = Array.isArray(data) ? data : [];
  const [busy, setBusy] = useState({});  // id → boolean
  const [confirmId, setConfirmId] = useState(null);

  const fmt = (iso) => {
    if (!iso) return '\u2014';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const setStatus = async (id, status) => {
    setBusy(b => ({ ...b, [id]: true }));
    try {
      const token = tokenStore.getItem(TOKEN_KEY);
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      await reload();
    } catch {} finally { setBusy(b => ({ ...b, [id]: false })); }
  };

  const doDelete = async (id) => {
    setBusy(b => ({ ...b, [id]: true }));
    try {
      const token = tokenStore.getItem(TOKEN_KEY);
      await axios.delete(`${API}/admin/enquiries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setConfirmId(null);
      await reload();
    } catch {} finally { setBusy(b => ({ ...b, [id]: false })); }
  };

  // counts by status
  const counts = useMemo(() => {
    const c = { new: 0, in_progress: 0, complete: 0 };
    rows.forEach(e => { c[e.status || 'new'] = (c[e.status || 'new'] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <section data-testid="admin-enquiries">
      <SectionHeader eyebrow="Enquiries" title={`${rows.length} submission${rows.length === 1 ? '' : 's'}`} onRefresh={reload} loading={loading} />

      {/* status summary */}
      {rows.length > 0 && (
        <div className="grid grid-cols-3 gap-3 sm:gap-5 mb-8">
          {STATUS_OPTIONS.map(opt => (
            <div key={opt.value} className="bg-[color:var(--tdt-cream)] rounded-md p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full" style={{ background: STATUS_STYLE[opt.value].dot }} />
                <span className="tdt-eyebrow">{opt.label}</span>
              </div>
              <div className="font-serif text-2xl sm:text-3xl text-[color:var(--tdt-ink)]">{counts[opt.value] || 0}</div>
            </div>
          ))}
        </div>
      )}

      {!rows.length ? (
        <div className="bg-[color:var(--tdt-cream)] rounded-md p-10 text-sm text-[color:var(--tdt-muted)]">
          {loading ? 'Loading\u2026' : 'No enquiries yet.'}
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((e) => {
            const status = e.status || 'new';
            const style = STATUS_STYLE[status] || STATUS_STYLE.new;
            const isBusy = !!busy[e.id];
            return (
              <article key={e.id} data-testid={`enquiry-${e.id}`} className="bg-[color:var(--tdt-cream)] rounded-md p-5 sm:p-8">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="font-serif text-xl sm:text-2xl text-[color:var(--tdt-ink)]">{e.name}</h3>
                      <span data-testid={`enquiry-status-${e.id}`} className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 rounded-full ring-1 ${style.text} ${style.ring}`}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: style.dot }} />
                        {STATUS_OPTIONS.find(o => o.value === status)?.label || status}
                      </span>
                    </div>
                    <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--tdt-muted)]">{fmt(e.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      data-testid={`enquiry-status-select-${e.id}`}
                      disabled={isBusy}
                      value={status}
                      onChange={(ev) => setStatus(e.id, ev.target.value)}
                      className="text-xs tracking-[0.16em] uppercase bg-transparent border border-[color:var(--tdt-border)] rounded-full px-3 py-2 focus:outline-none focus:border-[color:var(--tdt-gold)] disabled:opacity-50"
                    >
                      {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button
                      data-testid={`enquiry-delete-${e.id}`}
                      disabled={isBusy}
                      onClick={() => setConfirmId(e.id)}
                      className="text-xs tracking-[0.16em] uppercase border border-[color:var(--tdt-border)] hover:border-[#b03a3a] hover:text-[#b03a3a] text-[color:var(--tdt-muted)] rounded-full px-3 py-2 transition-colors disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                  <Field label="Email" value={<a className="hover:text-[color:var(--tdt-gold)]" href={`mailto:${e.email}`}>{e.email}</a>} />
                  <Field label="Phone" value={e.phone} />
                  <Field label="Occasion" value={e.occasion} />
                  <Field label="Guests" value={e.guests} />
                  <Field label="Preferred date" value={e.preferred_date} />
                  <Field label="Location" value={e.location} />
                  <Field label="Style" value={e.experience_style} />
                  <Field label="Budget" value={e.budget_range} />
                  <Field label="Privacy" value={e.privacy_level} />
                  <Field label="Transport" value={e.transport} />
                </div>
                {(e.food_preferences || e.beverage_preferences || e.dietary || e.notes) && (
                  <div className="mt-5 pt-5 border-t border-[color:var(--tdt-border)] grid sm:grid-cols-2 gap-x-10 gap-y-4 text-sm">
                    {e.food_preferences && <Field label="Food preferences" value={e.food_preferences} />}
                    {e.beverage_preferences && <Field label="Beverages" value={e.beverage_preferences} />}
                    {e.dietary && <Field label="Dietary" value={e.dietary} />}
                    {e.notes && <Field label="Notes" value={e.notes} />}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmId && (
        <div data-testid="delete-confirm" className="fixed inset-0 z-50 flex items-center justify-center px-5 bg-[color:var(--tdt-ink)]/70 backdrop-blur-sm">
          <div className="bg-[color:var(--tdt-ivory)] rounded-md max-w-md w-full p-7 sm:p-9 text-center">
            <div className="tdt-eyebrow mb-3">Confirm deletion</div>
            <h3 className="font-serif text-2xl sm:text-3xl text-[color:var(--tdt-ink)] mb-3">Are you sure?</h3>
            <p className="text-sm text-[color:var(--tdt-muted)] mb-8 leading-relaxed">
              This enquiry will be permanently removed. This cannot be undone.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-center">
              <button data-testid="delete-cancel" onClick={() => setConfirmId(null)} className="btn-pill btn-outline-ink text-xs">Cancel</button>
              <button
                data-testid="delete-confirm-btn"
                onClick={() => doDelete(confirmId)}
                disabled={busy[confirmId]}
                className="btn-pill text-xs text-[color:var(--tdt-ivory)] disabled:opacity-60"
                style={{ background: '#b03a3a', borderColor: '#b03a3a' }}
              >
                {busy[confirmId] ? 'Deleting\u2026' : 'Delete enquiry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="tdt-label">{label}</div>
      <div className="text-[color:var(--tdt-ink)] font-serif text-base whitespace-pre-line">{value}</div>
    </div>
  );
}

// ============ Pricing ============
function Pricing() {
  const { data, loading, reload } = useFetch('/journeys');
  const journeys = Array.isArray(data) ? data : [];
  const [drafts, setDrafts] = useState({});
  const [savingSlug, setSavingSlug] = useState(null);
  const [savedSlug, setSavedSlug] = useState(null);

  // When data arrives, hydrate drafts so we have one editable copy per journey.
  useEffect(() => {
    if (!journeys.length) return;
    setDrafts((cur) => {
      const next = { ...cur };
      journeys.forEach((j) => {
        if (!next[j.slug]) {
          next[j.slug] = {
            name: j.name || '',
            short: j.short || '',
            duration: j.duration || '',
            guests: j.guests || '',
            bestFor: j.bestFor || '',
            investment: j.investment || '',
            long: j.long || '',
          };
        }
      });
      return next;
    });
  }, [journeys.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (slug, field, value) => {
    setDrafts((d) => ({ ...d, [slug]: { ...d[slug], [field]: value } }));
  };

  const save = async (slug) => {
    setSavingSlug(slug); setSavedSlug(null);
    try {
      const token = tokenStore.getItem(TOKEN_KEY);
      await axios.patch(`${API}/admin/journeys/${slug}`, drafts[slug], { headers: { Authorization: `Bearer ${token}` } });
      invalidateJourneys();
      setSavedSlug(slug);
      await reload();
      setTimeout(() => setSavedSlug(null), 2500);
    } catch (e) {
      // simple inline feedback
    } finally { setSavingSlug(null); }
  };

  return (
    <section data-testid="admin-pricing">
      <SectionHeader eyebrow="Pricing & Journeys" title="Maintain prices and copy" onRefresh={reload} loading={loading} />

      {!journeys.length ? (
        <div className="bg-[color:var(--tdt-cream)] rounded-md p-10 text-sm text-[color:var(--tdt-muted)]">
          {loading ? 'Loading\u2026' : 'No journeys yet.'}
        </div>
      ) : (
        <div className="space-y-5">
          {journeys.map((j) => {
            const d = drafts[j.slug] || {};
            const saving = savingSlug === j.slug;
            const saved = savedSlug === j.slug;
            return (
              <article key={j.slug} data-testid={`pricing-${j.slug}`} className="bg-[color:var(--tdt-cream)] rounded-md p-5 sm:p-8">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                  <div className="flex items-baseline gap-4 min-w-0">
                    <span className="font-serif text-2xl text-[color:var(--tdt-gold)]">{j.number}</span>
                    <div className="min-w-0">
                      <h3 className="font-serif text-xl sm:text-2xl text-[color:var(--tdt-ink)] truncate">{j.name}</h3>
                      <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--tdt-muted)] mt-1">{j.slug}</div>
                    </div>
                  </div>
                  <button
                    data-testid={`pricing-save-${j.slug}`}
                    disabled={saving}
                    onClick={() => save(j.slug)}
                    className="btn-pill btn-gold text-xs shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving\u2026' : saved ? <>Saved <Save size={14} /></> : <>Save changes <Save size={14} /></>}
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                  <PField label="Investment (price line shown publicly)" value={d.investment} onChange={(v) => update(j.slug, 'investment', v)} />
                  <PField label="Duration" value={d.duration} onChange={(v) => update(j.slug, 'duration', v)} />
                  <PField label="Guests" value={d.guests} onChange={(v) => update(j.slug, 'guests', v)} />
                  <PField label="Best for" value={d.bestFor} onChange={(v) => update(j.slug, 'bestFor', v)} />
                  <PField label="Name" value={d.name} onChange={(v) => update(j.slug, 'name', v)} className="sm:col-span-2" />
                  <PField label="Short description (card)" value={d.short} onChange={(v) => update(j.slug, 'short', v)} className="sm:col-span-2" multiline />
                  <PField label="Full description (Signature Journeys page)" value={d.long} onChange={(v) => update(j.slug, 'long', v)} className="sm:col-span-2" multiline rows={4} />
                </div>
              </article>
            );
          })}
        </div>
      )}

      <p className="mt-8 text-[10px] uppercase tracking-[0.3em] text-[color:var(--tdt-muted)]">Changes save immediately and update the public Home and Signature Journeys pages.</p>
    </section>
  );
}

function PField({ label, value, onChange, multiline, rows = 2, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="tdt-label">{label}</span>
      {multiline ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full bg-transparent border border-[color:var(--tdt-border)] focus:border-[color:var(--tdt-gold)] outline-none rounded-sm p-3 text-[color:var(--tdt-ink)] font-serif text-base"
        />
      ) : (
        <input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border border-[color:var(--tdt-border)] focus:border-[color:var(--tdt-gold)] outline-none rounded-sm px-3 py-2 text-[color:var(--tdt-ink)] font-serif text-base"
        />
      )}
    </label>
  );
}

